<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">

    <div class="filter my-3">
      <div class="filter-head">
        <h4>واتساب — {{ group?.name ?? '...' }}</h4>
        <div class="buttons">
          <router-link :to="`/grades/${gradeSlug}/groups/${groupId}`" class="btn btn-outline-primary">
            <i class="pi pi-arrow-right me-1"></i> رجوع لطلاب المجموعة
          </router-link>
        </div>
      </div>
    </div>

    <!-- ── Group linking ── -->
    <div class="card p-3 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <strong>حالة الربط: </strong>
          <span v-if="group?.whatsapp_linked" class="status-badge status-badge--accepted">
            مرتبطة — {{ group.whatsapp_group_name }}
          </span>
          <span v-else class="status-badge status-badge--pending">غير مرتبطة بعد</span>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary" :disabled="syncing" @click="syncGroups">
            <i class="pi pi-spin pi-spinner" v-if="syncing"></i>
            <i class="pi pi-refresh me-1" v-else></i> مزامنة مجموعات الواتساب
          </button>
          <button class="btn PrimaryButton" :disabled="group?.whatsapp_linked || creating" @click="createWhatsAppGroup">
            <i class="pi pi-spin pi-spinner-dotted" v-if="creating"></i>
            {{ creating ? 'جاري الإنشاء...' : 'إنشاء مجموعة واتساب' }}
          </button>
        </div>
      </div>

      <!-- ── Large-class participant progress (>256 members) ── -->
      <div v-if="group?.whatsapp_linked && progress && !progress.completed" class="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-3 border-top">
        <div class="text-muted">
          تمت إضافة {{ progress.added }} عضو، ويتبقى {{ progress.remaining }} عضو لم تتم إضافتهم بعد (تجاوز الحد 256 لدى الإنشاء)
        </div>
        <button class="btn btn-outline-primary" :disabled="continuing" @click="continueAddingParticipants">
          <i class="pi pi-spin pi-spinner-dotted" v-if="continuing"></i>
          {{ continuing ? 'جاري إضافة الباقي...' : 'استكمال إضافة الأعضاء' }}
        </button>
      </div>
    </div>

    <!-- ── Send to group ── -->
    <div class="card p-3 mb-4" v-if="group?.whatsapp_linked">
      <h5 class="mb-3">إرسال رسالة إلى المجموعة</h5>
      <div class="form-group mb-2">
        <textarea class="form-control" rows="3" v-model="groupMessage" placeholder="اكتب الرسالة هنا"></textarea>
      </div>
      <div class="row g-2 align-items-end">
        <div class="col-md-9 col-12">
          <label>الملف (اختياري)</label>
          <input type="file" class="form-control" @change="e => groupMediaFile = e.target.files[0] || null" />
        </div>
        <div class="col-md-3 col-12">
          <button class="btn PrimaryButton w-100" :disabled="sendingGroup" @click="sendGroupMessage">
            <i class="pi pi-spin pi-spinner-dotted" v-if="sendingGroup"></i>
            {{ sendingGroup ? 'جاري الإرسال...' : 'إرسال للمجموعة' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Send direct message ── -->
    <div class="card p-3 mb-4">
      <h5 class="mb-3">إرسال رسالة مباشرة لأولياء الأمور</h5>

      <div class="row g-2 mb-3">
        <div class="col-md-8 col-12 d-flex flex-wrap gap-2">
          <button class="btn btn-sm" :class="recipientMode==='all' ? 'btn-primary' : 'btn-outline-primary'" @click="applyRecipientMode('all')">الكل</button>
          <button class="btn btn-sm" :class="recipientMode==='accepted' ? 'btn-success' : 'btn-outline-success'" @click="applyRecipientMode('accepted')">المقبولون فقط</button>
          <button class="btn btn-sm" :class="recipientMode==='pending' ? 'btn-warning text-white' : 'btn-outline-warning'" @click="applyRecipientMode('pending')">المعلقون فقط</button>
          <button class="btn btn-sm" :class="recipientMode==='rejected' ? 'btn-danger' : 'btn-outline-danger'" @click="applyRecipientMode('rejected')">المرفوضون فقط</button>
        </div>
        <div class="col-md-4 col-12 text-md-end">
          <span class="text-muted">تم اختيار {{ selectedIds.length }} من {{ recipientCandidates.length }}</span>
        </div>
      </div>

      <div class="recipients-list mb-3">
        <div v-if="loadingStudents" class="text-center py-3">
          <i class="pi pi-spin pi-spinner"></i>
        </div>
        <div v-else-if="recipientCandidates.length === 0" class="text-muted py-2">لا يوجد طلاب لديهم رقم هاتف ولي أمر صالح</div>
        <div v-else class="row">
          <div class="col-md-4 col-6" v-for="s in recipientCandidates" :key="s.id">
            <label class="d-flex align-items-center gap-2">
              <input type="checkbox" :checked="selectedIds.includes(s.id)" @change="toggleStudent(s.id)" />
              <span>{{ s.name }} — {{ s.parent_phone }}</span>
            </label>
          </div>
        </div>
      </div>

      <div class="form-group mb-2">
        <textarea class="form-control" rows="3" v-model="directMessage" placeholder="اكتب الرسالة هنا"></textarea>
      </div>
      <div class="row g-2 align-items-end">
        <div class="col-md-9 col-12">
          <label>الملف (اختياري)</label>
          <input type="file" class="form-control" @change="e => directMediaFile = e.target.files[0] || null" />
        </div>
        <div class="col-md-3 col-12">
          <button class="btn PrimaryButton w-100" :disabled="sendingDirect || selectedIds.length === 0" @click="sendDirectMessage">
            <i class="pi pi-spin pi-spinner-dotted" v-if="sendingDirect"></i>
            {{ sendingDirect ? 'جاري الإرسال...' : `إرسال مباشر (${selectedIds.length})` }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── History ── -->
    <div class="card p-3">
      <h5 class="mb-3">سجل الرسائل</h5>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>النوع</th>
            <th>الرسالة</th>
            <th>الوسائط</th>
            <th>الحالة</th>
            <th>التاريخ</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loadingHistory">
            <td colspan="6" class="text-center py-3"><i class="pi pi-spin pi-spinner"></i></td>
          </tr>
          <tr v-else-if="history.length === 0">
            <td colspan="6" class="text-center py-3 text-muted">لا يوجد سجل رسائل بعد</td>
          </tr>
          <tr v-for="(m, i) in history" :key="m.id" v-else>
            <td>{{ i + 1 }}</td>
            <td>{{ m.type === 'group' ? 'مجموعة' : 'مباشر' }}</td>
            <td>{{ (m.message || '').slice(0, 60) }}</td>
            <td>
              <a v-if="m.media_path" :href="mediaUrl(m)" target="_blank">{{ m.media_type }}</a>
              <span v-else>—</span>
            </td>
            <td>
              <span v-if="m.status === 'sent'" class="status-badge status-badge--accepted">تم الإرسال</span>
              <span v-else-if="m.status === 'failed'" class="status-badge status-badge--rejected">فشل</span>
              <span v-else class="status-badge status-badge--pending">قيد الإرسال</span>
            </td>
            <td>{{ formatDate(m.created_at) }}</td>
          </tr>
        </tbody>
      </table>
      <pagination v-if="historyPaginator" :data="historyPaginator" class="mx-auto d-flex align-items-center justify-content-center pagination" @pagination-change-page="fetchHistory"></pagination>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';

export default {
  name: 'GradeGroupWhatsApp',
  components: { pagination, Breadcrumb },

  data() {
    return {
      group: null,
      students: [],
      loadingStudents: true,

      recipientMode: 'all',
      selectedIds: [],

      groupMessage: '',
      groupMediaFile: null,
      sendingGroup: false,

      directMessage: '',
      directMediaFile: null,
      sendingDirect: false,

      creating: false,
      syncing: false,
      continuing: false,
      progress: null,

      history: [],
      historyPaginator: null,
      loadingHistory: true,
    };
  },

  computed: {
    gradeSlug() { return this.$route.params.gradeSlug; },
    groupId() { return this.$route.params.groupId; },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'مجموعات الطلاب', path: `/grades/${this.gradeSlug}/groups` },
        { name: this.group?.name ?? 'المجموعة', path: `/grades/${this.gradeSlug}/groups/${this.groupId}` },
        { name: 'واتساب', path: `/grades/${this.gradeSlug}/groups/${this.groupId}/whatsapp` },
      ];
    },
    recipientCandidates() {
      return this.students.filter(s => s.parent_phone);
    },
  },

  methods: {
    formatDate(d) {
      return d ? new Date(d).toLocaleString('ar-EG') : '';
    },

    mediaUrl(m) {
      const apiBase = axios.defaults.baseURL || '';
      return apiBase.replace(/\/api\/?$/, '') + '/' + m.media_path;
    },

    async fetchGroup() {
      const { data } = await axios.get(`/groups/${this.groupId}`);
      this.group = data.data;
    },

    async fetchStudents() {
      this.loadingStudents = true;
      try {
        const { data } = await axios.get(`/groups/${this.groupId}/students`);
        this.students = data.data ?? [];
      } catch {
        this.students = [];
      } finally {
        this.loadingStudents = false;
        this.applyRecipientMode(this.recipientMode);
      }
    },

    applyRecipientMode(mode) {
      this.recipientMode = mode;
      if (mode === 'all') {
        this.selectedIds = this.recipientCandidates.map(s => s.id);
      } else {
        const statusMap = { accepted: 1, pending: 0, rejected: 2 };
        this.selectedIds = this.recipientCandidates
          .filter(s => s.reservation_status === statusMap[mode])
          .map(s => s.id);
      }
    },

    toggleStudent(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },

    async createWhatsAppGroup() {
      this.creating = true;
      try {
        const { data } = await axios.post(`/groups/${this.groupId}/whatsapp/create`);
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        if (data.status) {
          await this.fetchGroup();
          await this.fetchProgress();
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'حدث خطأ', 'error');
      } finally {
        this.creating = false;
      }
    },

    async fetchProgress() {
      if (!this.group?.whatsapp_linked) {
        this.progress = null;
        return;
      }
      try {
        const { data } = await axios.get(`/groups/${this.groupId}/whatsapp/participants-progress`);
        this.progress = data.data;
      } catch {
        this.progress = null;
      }
    },

    async continueAddingParticipants() {
      this.continuing = true;
      try {
        const { data } = await axios.post(`/groups/${this.groupId}/whatsapp/continue-participants`);
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        if (data.status) {
          await this.fetchProgress();
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'حدث خطأ', 'error');
      } finally {
        this.continuing = false;
      }
    },

    async syncGroups() {
      this.syncing = true;
      try {
        const { data } = await axios.post('/groups/whatsapp/sync');
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        await this.fetchGroup();
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'حدث خطأ', 'error');
      } finally {
        this.syncing = false;
      }
    },

    async sendGroupMessage() {
      this.sendingGroup = true;
      try {
        const fd = new FormData();
        fd.append('message', this.groupMessage || '');
        if (this.groupMediaFile) fd.append('media', this.groupMediaFile);

        const { data } = await axios.post(`/groups/${this.groupId}/whatsapp/send-group`, fd);
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        if (data.status) {
          this.groupMessage = '';
          this.groupMediaFile = null;
          this.fetchHistory();
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'فشل الإرسال', 'error');
      } finally {
        this.sendingGroup = false;
      }
    },

    async sendDirectMessage() {
      this.sendingDirect = true;
      try {
        const phones = this.students
          .filter(s => this.selectedIds.includes(s.id) && s.parent_phone)
          .map(s => s.parent_phone);

        const fd = new FormData();
        fd.append('message', this.directMessage || '');
        phones.forEach(p => fd.append('phones[]', p));
        if (this.directMediaFile) fd.append('media', this.directMediaFile);

        const { data } = await axios.post(`/groups/${this.groupId}/whatsapp/send-direct`, fd);
        Swal.fire('', data.message, data.status ? 'success' : 'error');
        if (data.status) {
          this.directMessage = '';
          this.directMediaFile = null;
          this.fetchHistory();
        }
      } catch (error) {
        Swal.fire('', error.response?.data?.message || 'فشل الإرسال', 'error');
      } finally {
        this.sendingDirect = false;
      }
    },

    async fetchHistory(page = 1) {
      this.loadingHistory = true;
      try {
        const { data } = await axios.get(`/groups/${this.groupId}/whatsapp/messages`, { params: { page, per_page: 10 } });
        this.historyPaginator = data.data;
        this.history = data.data.data ?? [];
      } catch {
        this.history = [];
      } finally {
        this.loadingHistory = false;
      }
    },
  },

  async mounted() {
    await this.fetchGroup();
    await this.fetchStudents();
    this.fetchHistory();
    this.fetchProgress();
  },
};
</script>

<style scoped>
.recipients-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 0.75rem;
}
</style>
